import React from 'react';

import {withParams} from "../../App";
import PageComponent from "../pageComponent/pageComponent";

// Canonical tutorial topics shared by every Python ML framework. Each framework
// resolves the same slugs against its own content directory so the landing pages,
// routes and HTML sources all line up. See public/ml/tutorials/<framework>/.
export const TOPICS = [
    {slug: 'getting-started', title: 'Getting started', tags: ['Getting Started']},
    {slug: 'getting-started-ide', title: 'Working in an IDE', tags: ['IDE']},
    {slug: 'interactive-notebook', title: 'Interactive notebooks', tags: ['Notebook']},
    {slug: 'iris-classification', title: 'Iris classification', tags: ['Classification']},
    {slug: 'binary-classification', title: 'Binary classification', tags: ['Classification']},
    {slug: 'image-classification', title: 'Image classification', tags: ['Classification', 'Images']},
    {slug: 'linear-regression', title: 'Linear regression', tags: ['Regression']},
    {slug: 'time-series', title: 'Time series analysis', tags: ['Time Series']},
    {slug: 'stock-price-prediction', title: 'Stock price prediction', tags: ['Time Series', 'Forecasting']},
    {slug: 'nlp-basics', title: 'NLP basics', tags: ['NLP']},
    {slug: 'housing-regression', title: 'House price prediction', tags: ['Regression']},
    {slug: 'feature-engineering', title: 'Feature engineering', tags: ['Data', 'Features'],
        dataFile: '/data/explore/ames-housing-sample.csv?label=SalePrice'},
    {slug: 'data-cleansing', title: 'Data cleansing', tags: ['Data', 'Data Cleansing']},
    {slug: 'simulation', title: 'Simulation and synthetic data', tags: ['Data', 'Simulation']},
    {slug: 'dimensionality-reduction', title: 'Dimensionality reduction', tags: ['Data', 'Dimensionality Reduction']},
];

export const FRAMEWORKS = {
    'scipy': {label: 'SciPy', languages: ['Python']},
    'scikit-learn': {label: 'Scikit-Learn', languages: ['Python']},
    'pytorch': {label: 'PyTorch', languages: ['Python']},
    'tensorflow': {label: 'TensorFlow', languages: ['Python']},
};

// Build the slug -> content mapping for a single framework.
export function buildSlugs(framework) {
    const {label, languages} = FRAMEWORKS[framework];
    return TOPICS.map((topic) => ({
        slug: topic.slug,
        source: `/ml/tutorials/${framework}/${framework}-${topic.slug}.html`,
        title: `${topic.title} with ${label}`,
        path: `/machine-learning/tutorials/${framework}/`,
        tags: topic.tags,
        languages,
        sourceLinks: [],
        algos: [],
        attributed: [],
        dataFile: topic.dataFile,
    }));
}

// Generic tutorial page for the Python ML frameworks (SciPy, Scikit-Learn,
// PyTorch, TensorFlow). The framework is supplied by the route so the same
// component serves every framework consistently.
const FrameworkPage = ({framework, params}) => {
    const meta = FRAMEWORKS[framework];
    const slugs = buildSlugs(framework);

    const slug = params?.slug || window.location.pathname.split('/').filter(Boolean).slice(-1)[0] || '';
    const current = slugs.find((item) => item.slug === slug) || {
        title: meta.label,
        tags: [],
        sourceLinks: [],
        languages: meta.languages,
        algos: [],
        attributed: [],
        source: '',
        path: `/machine-learning/tutorials/${framework}/`,
    };

    if (!slugs.some((item) => item.slug === slug)) {
        console.error(`FrameworkPage (${framework}) failed to locate HTML path for slug : ${slug}`);
    }

    const topicPages = slugs
        .filter((item) => item.slug !== slug)
        .map((item) => ({title: item.title, slug: item.slug, path: item.path}));

    const state = {
        nextContent: {key: '', slug: '', title: meta.label, path: `/machine-learning/tutorials/${framework}/`},
        relatedContent: [],
        topicPages,
        title: current.title,
        tags: current.tags,
        sourceLinks: current.sourceLinks,
        languages: current.languages,
        algos: current.algos !== undefined ? current.algos : [],
        suggestedCourses: [],
        attributed: current.attributed,
        source: current.source,
        html: '',
        dataFile: current.dataFile,
        download: current.download,
    };

    return (
        <PageComponent props={state} page={`${meta.label} ML Page`}/>
    );
};

export default withParams(FrameworkPage);
